import React, { useCallback, useEffect, useRef, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-quartz.css'
import { useLocation, useNavigate } from 'react-router-dom'
import { AutoComplete } from 'primereact/autocomplete'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import i18next from 'i18next'
import { Calendar } from 'primereact/calendar'
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa6'

import { debounce } from '../../helpers/debounce'
import validSupergitHIS from '../../api/validSupergitHIS'
import SharedButton from '../../components/Shared/SharedButton'
import { convertToArabicNumerals } from '../../uitls/ConvertToArNumber'
import moment from 'moment'

const getAccountName = (account, lang) => {
  if (!account) return '';
  return lang === 'ar'
    ? `${account.name_ar} ${account.code}`
    : `${account.name_en} ${account.code}`;
};

const moveHeaderFocusUpDown = (previousHeader, headerRowCount, isUp) => {
  const previousColumn = previousHeader.column
  const isSpanHeaderHeight =
    !!previousColumn.isSpanHeaderHeight && previousColumn.isSpanHeaderHeight()
  const lastRowIndex = previousHeader.headerRowIndex
  let nextRowIndex = isUp ? lastRowIndex - 1 : lastRowIndex + 1
  let nextColumn
  if (nextRowIndex === -1) {
    return previousHeader
  }
  if (nextRowIndex === headerRowCount) {
    nextRowIndex = -1
  }
  let parentColumn = previousColumn.getParent()
  if (isUp) {
    if (isSpanHeaderHeight) {
      while (parentColumn && parentColumn.isPadding()) {
        parentColumn = parentColumn.getParent()
      }
    }
    if (!parentColumn) {
      return previousHeader
    }
    nextColumn = parentColumn
  } else {
    const children =
      (previousColumn.getChildren && previousColumn.getChildren()) || []
    nextColumn = children.length > 0 ? children[0] : previousColumn
  }
  return {
    headerRowIndex: nextRowIndex,
    column: nextColumn
  }
}

const AccountCellEditor = props => {
  const [value, setValue] = useState(props.value || '')
  const lang = i18next.language
  const highlightedIndexRef = useRef(null)
  const [accountSuggestions, setAccountSuggestions] = useState([])
  const [accLoading, setAccLoading] = useState(false)

  const handleAccountSearch = event => {
    if (event?.query?.length > 2) {
      debouncedSearchAccoutns(event?.query)
    }
  }

  const debouncedSearchAccoutns = useCallback(
    debounce(async search => {
      try {
        setAccLoading(true)
        const response = await validSupergitHIS.post('searchJournalAccounts', {
          query: search
        })
        const listAccountsValues =
          response?.data && response?.data?.data
            ? Object.entries(response?.data?.data).map(([id, account]) => ({
              value: Number(account?.id),
              label: getAccountName(account, lang),
              is_vat_out: account?.is_vat_out,
              is_vat_in: account?.is_vat_in,
              original: account
            }))
            : []
        setAccountSuggestions(listAccountsValues)
      } catch (error) {
        console.log(error)
        setAccountSuggestions([])
      } finally {
        setAccLoading(false)
      }
    }, 500),
    [lang]
  )

  const handleChange = e => {
    setValue(e.value)
    if (e?.value?.label) {
      let label = e?.value?.label
      props.node.setDataValue('account', label)
      props.node.setDataValue('accountId', e?.value?.value)
      props.api.stopEditing({ newValue: label })
      highlightedIndexRef.current = null
    }
  }

  const handleKeyDown = e => {
    if (e.key === 'ArrowDown') {
      highlightedIndexRef.current =
        highlightedIndexRef.current === null ||
          highlightedIndexRef.current === accountSuggestions.length - 1
          ? 0
          : highlightedIndexRef.current + 1
    } else if (e.key === 'ArrowUp') {
      highlightedIndexRef.current =
        highlightedIndexRef.current === null ||
          highlightedIndexRef.current === 0
          ? accountSuggestions.length - 1
          : highlightedIndexRef.current - 1
    }
  }

  const handleBlur = e => {
    const selectedItem = accountSuggestions[highlightedIndexRef.current]
    if (selectedItem) {
      setValue(selectedItem)
      props.node.setDataValue('account', selectedItem.label)
      props.node.setDataValue('accountId', selectedItem.value)
      props.api.stopEditing({ newValue: selectedItem.label })
      highlightedIndexRef.current = null
    }
  }

  return (
    <AutoComplete
      value={value}
      suggestions={accountSuggestions}
      completeMethod={handleAccountSearch}
      field='label'
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      className='w-full'
      forceSelection
      autoFocus
      pt={{
        input: {
          root: { className: 'w-full border-0 !outline-none !shadow-none !py-0 !h-[25px]' }
        },
        panel: {
          className:
            '!bg-gray-100 !shadow-lg rounded-[8px] mt-2 pb-2 overflow-hidden'
        },
        loadingIcon: {
          className: `absolute top-[14px] ${lang == 'ar' ? '!left-[12px]' : 'right-[12px]'} ${accLoading ? '' : 'hidden'}`
        }
      }}
    />
  )
}

const OpeningBalances = () => {
  const { t, i18n } = useTranslation()
  const lang = i18next.language
  const gridRef = useRef()
  const navigate = useNavigate()
  const location = useLocation()
  const returnActiveTab = location.state?.activeTab || 'opening_balance'

  const [isEdit, setIsEdit] = useState(false);
  const [journalEntryId, setJournalEntryId] = useState(null);
  const [goLiveDate, setGoLiveDate] = useState(new Date(new Date().getFullYear(), 0, 1))
  const [rowData, setRowData] = useState([])
  const [columnDefs, setColumnDefs] = useState([])
  const [isRtl, setIsRtl] = useState(lang === 'ar' || lang === 'he')
  const [debitTotal, setDebitTotal] = useState(0)
  const [creditTotal, setCreditTotal] = useState(0)
  const [allAccounts, setAllAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchOpeningBalance = async () => {
      setIsLoading(true);
      try {
        const response = await validSupergitHIS.post(`transaction_entry/${location.state.transactionData.id}`);
        
        if (response?.data?.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
          const entries = response.data.data;
          setIsEdit(true);
          setJournalEntryId(entries[0]?.journal_entry_id);

          if (location?.state?.transactionData?.date) {
            setGoLiveDate(new Date(location?.state?.transactionData?.date));
          }

          const balances = entries
            .filter(entry => entry.account_name_en !== "Opening Balance Equity")
            .map(entry => ({
              account: lang === 'ar' ? `${entry.account_name_ar} ${entry.account_code}` : `${entry.account_name_en} ${entry.account_code}`,
              debitAmount: entry.entry_type?.toLowerCase() === 'debit' ? Number(entry.amount) : 0,
              creditAmount: entry.entry_type?.toLowerCase() === 'credit' ? Number(entry.amount) : 0,
              accountId: entry.account_id,
              isEquity: false
            }));

          const dTotal = balances.reduce((sum, b) => sum + b.debitAmount, 0);
          const cTotal = balances.reduce((sum, b) => sum + b.creditAmount, 0);
          setDebitTotal(dTotal);
          setCreditTotal(cTotal);

          const rows = [...balances];
          while (rows.length < 300) {
            rows.push({ account: '', debitAmount: 0, creditAmount: 0 });
          }
          setRowData(rows);
        } else {
          setIsEdit(false);
          const defaultRows = Array.from({ length: 300 }, () => ({
            account: '',
            debitAmount: 0,
            creditAmount: 0
          }))
          setRowData(defaultRows)
          setDebitTotal(0)
          setCreditTotal(0)
        }
      } catch (e) {
        console.error("Error fetching opening balance:", e);
        console.error("Error response:", e?.response);
        
        // 404 means not found, which is fine, show empty form
        if (e?.response?.status === 404) {
          setIsEdit(false);
          const defaultRows = Array.from({ length: 300 }, () => ({
            account: '',
            debitAmount: 0,
            creditAmount: 0
          }))
          setRowData(defaultRows)
          setDebitTotal(0)
          setCreditTotal(0)
        } else {
          toast.error("Failed to load opening balance details");
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (location.state?.transactionData?.id) {
      fetchOpeningBalance();
    } else {
      setIsEdit(false);
      setJournalEntryId(null);
      const defaultRows = Array.from({ length: 300 }, () => ({
        account: '',
        debitAmount: 0,
        creditAmount: 0
      }))
      setRowData(defaultRows)
      setDebitTotal(0)
      setCreditTotal(0)
      setIsLoading(false);
    }
  }, [lang, location.state]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await validSupergitHIS.post('searchJournalAccounts', { query: '' });
        if (response?.data?.data) {
          setAllAccounts(Object.values(response.data.data));
        }
      } catch (e) {
        console.error("Failed to fetch accounts", e);
      }
    }
    fetchData()
  }, [])

  useEffect(() => {
    setIsRtl(lang === 'ar')

    const definitions = [
      {
        headerName: '',
        valueGetter: 'node.rowIndex + 1',
        width: 50,
        height: 25,
        suppressMenu: true,
        cellRenderer: params => {
          const rowIndex = params.node.rowIndex + 1
          return lang === 'ar' ? convertToArabicNumerals(rowIndex) : rowIndex
        }
      },
      {
        headerName: '',
        field: 'actions',
        width: 50,
        height: 25,
        suppressMenu: true,
        cellRenderer: 'DeleteButtonRenderer',
        cellClass: 'delete-cell'
      },
      {
        headerName: t('account'),
        field: 'account',
        editable: true,
        height: 25,
        cellEditor: AccountCellEditor,
        cellEditorParams: params => ({
          value: params?.value || ''
        }),
        minWidth: 300,
        flex: 2
      },
      {
        headerName: t('debitAmount'),
        editable: true,
        field: 'debitAmount',
        cellEditor: 'agNumberCellEditor',
        stopEditingWhenCellsLoseFocus: true,
        width: 150,
        height: 25
      },
      {
        headerName: t('creditAmount'),
        field: 'creditAmount',
        editable: true,
        cellEditor: 'agNumberCellEditor',
        stopEditingWhenCellsLoseFocus: true,
        width: 150,
        height: 25
      },
      {
        field: 'accountId',
        hide: true,
        editable: false
      }
    ]
    setColumnDefs(definitions)
  }, [lang, t])


  const DeleteButtonRenderer = props => (
    <button
      onClick={() => props.context.removeRow(props.node)}
      className='delete-btn'
    >
      ✕
    </button>
  )

  const handleCellValueChange = params => {
    const { colDef, newValue, node } = params
    const creditArray = []
    const debitArray = []

    if (colDef.field === 'creditAmount' || colDef.field === 'debitAmount') {
      gridRef.current?.api.forEachNode(rowNode => {
        const creditAmount = rowNode.data.creditAmount
        const debitAmount = rowNode.data.debitAmount
        if (colDef.field === 'creditAmount') {
          if (newValue && node?.rowIndex == rowNode?.rowIndex) {
            rowNode.setDataValue('debitAmount', 0)
          }
        } else if (colDef.field === 'debitAmount') {
          if (newValue && node?.rowIndex == rowNode?.rowIndex) {
            rowNode.setDataValue('creditAmount', 0)
          }
        }
        // Only push non-zero values
        if (creditAmount && Number(creditAmount) > 0) {
          creditArray.push(Number(creditAmount))
        }
        if (debitAmount && Number(debitAmount) > 0) {
          debitArray.push(Number(debitAmount))
        }
      })
      calculateTotals(creditArray, debitArray)
    }
  }

  const calculateTotals = (creditArray, debitArray) => {
    let dTotal = 0
    let cTotal = 0

    if (creditArray && debitArray) {
      cTotal = creditArray.reduce((sum, value) => sum + value, 0)
      dTotal = debitArray.reduce((sum, value) => sum + value, 0)
    } else {
      gridRef.current?.api.forEachNode(node => {
        dTotal += Number(node.data.debitAmount) || 0
        cTotal += Number(node.data.creditAmount) || 0
      })
    }

    setDebitTotal(dTotal)
    setCreditTotal(cTotal)
  }

  const gridOptions = {
    defaultColDef: {
      cellClass: 'border !border-gray-300 !border-t-0 !border-b-0 !border-r-0 py-0 flex items-center'
    },
    rowHeight: 25,
    onCellValueChanged: handleCellValueChange,
    context: {
      removeRow: rowNode => {
        const index = rowNode.rowIndex
        setRowData(prev => {
          const newRows = [...prev]
          newRows.splice(index, 1)
          while (newRows.length < 300) {
            newRows.push({ account: '', debitAmount: 0, creditAmount: 0 })
          }
          return newRows
        })
        setTimeout(() => calculateTotals(), 100)
      }
    },
    components: {
      DeleteButtonRenderer
    }
  }

  const tabToNextHeader = useCallback(params => {
    return moveHeaderFocusUpDown(
      params.previousHeaderPosition,
      params.headerRowCount,
      params.backwards
    )
  }, [])

  const navigateToNextCell = useCallback(params => {
    const previousCell = params.previousCellPosition,
      suggestedNextCell = params.nextCellPosition
    let nextRowIndex, renderedRowCount
    switch (params.key) {
      case 'ArrowDown':

        nextRowIndex = previousCell.rowIndex + 1
        if (nextRowIndex < -1) {
          return null
        }
        return {
          rowIndex: nextRowIndex,
          column: previousCell.column,
          rowPinned: previousCell.rowPinned
        }
      case 'ArrowUp':
        nextRowIndex = previousCell.rowIndex - 1
        renderedRowCount = params.api.getDisplayedRowCount()
        if (nextRowIndex >= renderedRowCount) {
          return null
        }
        return {
          rowIndex: nextRowIndex,
          column: previousCell.column,
          rowPinned: previousCell.rowPinned
        }
      case 'ArrowLeft':
      case 'ArrowRight':
        return suggestedNextCell
      default:
        throw Error(
          'this will never happen, navigation is always one of the 4 keys above'
        )
    }
  }, [])

  const handleSubmit = async () => {
    if (!goLiveDate) {
      toast.error('Please select a Go-Live Date')
      return
    }

    const balances = []
    let hasData = false

    gridRef.current?.api.forEachNode(node => {
      const { account, debitAmount, creditAmount, accountId } = node.data

      if (account && (Number(debitAmount) > 0 || Number(creditAmount) > 0)) {
        // Skip opening balance equity line as per API docs because backend calculates it automatically
        if (node.data.isEquity) return;
        let selectedAccount;

        if (accountId) {
          selectedAccount = allAccounts.find(
            acc => Number(acc.id) === Number(accountId)
          );
        }

        if (!selectedAccount) {
          selectedAccount = allAccounts.find(
            acc => getAccountName(acc, lang) === account
          )
        }

        if (selectedAccount) {
          balances.push({
            account_id: selectedAccount.id,
            debit: Number(debitAmount) || 0,
            credit: Number(creditAmount) || 0
          })
          hasData = true
        }
      }
    })

    if (!hasData) {
      toast.error('Please enter at least one opening balance')
      return
    }

    const payload = {
      go_live_date: moment(goLiveDate).format('YYYY-MM-DD'),
      balances: balances
    }

    if (isEdit && journalEntryId) {
      payload.journal_entry_id = Number(journalEntryId)
    }

    try {
      setIsLoading(true);
      let response;
      if (isEdit) {
        response = await validSupergitHIS.post('updateOpeningBalance', payload)
      } else {
        response = await validSupergitHIS.post('setOpeningBalance', payload)
      }

      if (response.data && !response.data.error) {
        toast.success(response.data.message || (isEdit ? 'Opening balance updated successfully' : 'Opening balance set successfully'))
        navigate(-1)
      } else {
        toast.error(response.data.error || 'Failed to process opening balances')
      }
    } catch (error) {
      console.error(error)
      toast.error(error?.response?.data?.error || 'An error occurred')
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className='rounded-md min-h-[85vh] overflow-hidden bg-gray-100 p-2 px-4 w-full'>
      <div className='flex gap-5 items-center mb-2'>
        <div className='flex justify-center items-center gap-5'>
          <div
            onClick={() => {
              navigate(-1)
            }}
            className='w-[30px] h-[30px]  lg:h-[35px] lg:w-[35px] text-primary flex justify-center items-center rounded-full bg-white cursor-pointer '
          >
            {i18n.language === 'en' ? (
              <FaArrowLeft size={18} />
            ) : (
              <FaArrowRight size={18} />
            )}
          </div>
          <h2 className='text-3xl min-w-[300px] font-bold text-gray-800'>
            {isEdit ? t('Edit Opening Balance') : t('Opening Balances Setup')}
          </h2>
        </div>
        <div className='grid grid-cols-2 gap-4 flex-grow'>
          <div className='flex flex-col gap-1'>
            <label className='text-base font-semibold'>
              {t('Go Live Date')}
            </label>
            <Calendar
              id='date'
              value={goLiveDate}
              onChange={(e) => setGoLiveDate(e.value)}
              showTime={false}
              dateFormat="dd-mm-yy"
              className='w-full !p-0 rounded-md hover:border-primary !outline-none !shadow-none'
              pt={{
                root: { className: '!h-[30px]' },
                input: { className: '!p-1 !h-[30px]' }
              }}
            />
          </div>
        </div>
        <div className='flex gap-4 justify-end'>
          <div>
            <span className='font-bold text-[18px]'>{t('Total Debit')}: </span>
            <span className='text-[18px]'>{debitTotal.toLocaleString()}</span>
          </div>
          <div>
            <span className='font-bold text-[18px]'>{t('Total Credit')}: </span>
            <span className='text-[18px]'>{creditTotal.toLocaleString()}</span>
          </div>
        </div>
      </div>

      <div className='ag-theme-quartz h-[75vh] overflow-y-auto mt-2'>
        <AgGridReact
          ref={gridRef}
          key={isRtl ? 'rtl' : 'ltr'}
          rowData={rowData}
          columnDefs={columnDefs}
          enableRtl={isRtl}
          gridOptions={gridOptions}
          animateRows={true}
          singleClickEdit={true}
          tabToNextHeader={tabToNextHeader}
          navigateToNextCell={navigateToNextCell}
          frameworkComponents={{
            DeleteButtonRenderer
          }}
        />
      </div>

      <div className='mt-2'>
        <div className='flex justify-end px-4 py-2 bg-gray-200 rounded-md'>
          <SharedButton
            label={isEdit ? t('Update Opening Balances') : t('Save Opening Balances')}
            onClick={handleSubmit}
            loading={isLoading}
            className='bg-primary text-white px-6 py-2 rounded'
          />
        </div>
      </div>
    </div>
  )
}

export default OpeningBalances
